<script setup lang="ts">
import { computed, useAttrs } from "vue";
import { formatName } from "@/utils/Formatters.ts";
import type { TPatient } from "@/api/models/patient/patient";
import type { TPracticeStaff, TPracticeStaffBadge } from "@/api/models/practice/practiceStaff";
import type { TPractice } from "@/api/models/practice/practice";
import type { TCompany } from "@/api/models/company/company";
import { usePracticeStaff } from "@/composables/models/practice/usePracticeStaff.ts";
import { usePatient } from "@/composables/models/patient/usePatient.ts";
import Badges from "@/components/shared/Badges.vue";
import type { TFile } from "@/api/shared/file";
import AppImg from "@/components/shared/files/AppImg.vue";
import { getPatientAvatarColor } from "@/utils/patientAvatarColor.ts";

type TLocation = "top" | "bottom" | "left" | "right";
type TAvatarUser =
  | TPracticeStaff
  | TPatient
  | TPractice
  | TCompany
  | Pick<TPracticeStaff, "id" | "first_name" | "last_name">
  | { picture: TFile | null; picture_default: string | undefined };

function hasBadges(u: unknown): u is { badges: Array<TPracticeStaffBadge> } {
  return Array.isArray((u as TPracticeStaff)?.badges);
}

const attrs = useAttrs();

const props = withDefaults(
  defineProps<{
    /** explicit label (wins for tooltip title) */
    userName?: string;
    location?: TLocation;

    /** 1) Provide full object OR 2) provide staffId/patientId */
    user?: TAvatarUser;
    staffId?: string;
    patientId?: string;
    patientStyle?: boolean;

    /** 4) fallback if no user/staff/patient image */
    imgSrc?: string;

    /** 5) fallback icon */
    icon?: string;
    iconSize?: number | string;

    size?: number | string;
    rounded?: string | boolean;

    enableTooltip?: boolean;
    hideBadges?: boolean;

    /** extra classes for avatar (recommended) */
    class?: string;

    overrideDefaultImage?: () => string | undefined | null;
    noImage?: boolean;
  }>(),
  {
    userName: undefined,
    location: "top",
    user: undefined,
    staffId: undefined,
    patientId: undefined,
    patientStyle: false,
    imgSrc: undefined,
    icon: undefined,
    size: 32,
    enableTooltip: false,
    noImage: false,
  }
);
/**
 * Fetch rules:
 * - if `user` is provided => don't fetch
 * - else if staffId exists => fetch staff (staff wins if both ids exist)
 * - else if patientId exists => fetch patient
 */
const shouldFetchStaff = computed(() => !props.user && !!props.staffId);
const shouldFetchPatient = computed(() => !props.user && !props.staffId && !!props.patientId);

const staffQuery = usePracticeStaff({
  id: props.staffId,
  enabled: shouldFetchStaff,
});

const patientQuery = usePatient(
  {
    id: computed(() => props.patientId),
    enabled: shouldFetchPatient,
  },
  { suppressErrors: true }
);

const resolvedUser = computed<TAvatarUser | null>(() => {
  // 1) user
  if (props.user) return props.user;

  // 2) staff
  if (staffQuery.data.value) return staffQuery.data.value;

  // 3) patient
  if (patientQuery.data.value) return patientQuery.data.value;

  return null;
});

const isLoading = computed(() => {
  if (props.user) return false;
  return (
    (shouldFetchStaff.value && staffQuery.isFetching.value) ||
    (shouldFetchPatient.value && patientQuery.isFetching.value)
  );
});

/**
 * Image priority:
 * 1) resolvedUser.picture/picture_default
 * 2) imgSrc
 * else null (icon/default icon will show)
 */
const image = computed(() => {
  if (props.noImage) return null;
  const u = resolvedUser.value as any;
  if (u && typeof u === "object") {
    const pic = u.picture as TFile | undefined;
    if (pic?.file) return pic.file;
    if (props.overrideDefaultImage) return props.overrideDefaultImage();
    if (u.picture_default) return u.picture_default;
  }
  return props.imgSrc ?? null;
});

/**
 * Name priority:
 * 1) props.userName
 * 2/3/4) formatName(resolvedUser)
 * fallback "Unknown"
 */
const displayName = computed(() => {
  const explicit = props.userName?.trim();
  if (explicit) return explicit;

  const u = resolvedUser.value;
  const fromUser = u ? formatName(u as any)?.trim() : "";
  return fromUser || "Unknown";
});

const getInitials = (firstName: string, lastName: string) => {
  const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : "";
  const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : "";
  return `${firstInitial}${lastInitial}`;
};

const displayInitials = computed(() => {
  const user = resolvedUser.value;
  if (
    user &&
    typeof user === "object" &&
    "first_name" in user &&
    typeof user.first_name === "string" &&
    "last_name" in user &&
    typeof user.last_name === "string"
  ) {
    return getInitials(user.first_name, user.last_name);
  }

  const parts = displayName.value
    .split(" ")
    .map((part) => part.trim())
    .filter(Boolean);
  const firstName = parts[0] ?? "";
  const lastName = parts.length > 1 ? (parts[parts.length - 1] ?? "") : "";

  return getInitials(firstName, lastName);
});

const avatarSizeNumber = computed(() => {
  const parsed = Number(props.size);
  return Number.isFinite(parsed) ? parsed : 32;
});
const isPatientAvatar = computed(() => {
  if (props.patientStyle) {
    return true;
  }

  if (props.patientId && !props.staffId) {
    return true;
  }

  const user = resolvedUser.value;
  return !!user && typeof user === "object" && "birth_date" in user;
});

const patientAvatarBgColor = computed(() => {
  if (!isPatientAvatar.value || image.value) return null;
  const u = resolvedUser.value as any;
  const id: string | undefined = u?.id ?? props.patientId;
  return id ? getPatientAvatarColor(id) : null;
});
const AVATAR_INITIALS_FONT_RATIO = 0.34;
const AVATAR_INITIALS_FONT_RATIO_NO_IMAGE = 0.28;
const AVATAR_INITIALS_MIN_SIZE_PX = 11;
const AVATAR_INITIALS_MAX_SIZE_PX = 26;
const initialsFontSize = computed(() => {
  const ratio =
    isPatientAvatar.value && !image.value
      ? AVATAR_INITIALS_FONT_RATIO_NO_IMAGE
      : AVATAR_INITIALS_FONT_RATIO;
  const scaledSize = avatarSizeNumber.value * ratio;
  const clampedSize = Math.min(
    AVATAR_INITIALS_MAX_SIZE_PX,
    Math.max(AVATAR_INITIALS_MIN_SIZE_PX, scaledSize)
  );
  return `${clampedSize}px`;
});

const badges = computed<TPracticeStaffBadge[]>(() => {
  const u = resolvedUser.value;
  if (!u || !hasBadges(u)) return [];
  return u.badges;
});

/** Merge classes from normal `class="..."` attribute + avatarClass prop */
const mergedAvatarClass = computed(() => {
  const fromAttrs = (attrs.class as any) ?? "";
  return [fromAttrs, props.class].filter(Boolean);
});
</script>

<template>
  <div
    v-tippy="{
      content: displayName,
      placement: location,
      disabled: !enableTooltip || !displayName,
    }"
    class="tw:relative tw:inline-block"
  >
    <v-avatar
      :size="size"
      :color="isPatientAvatar ? undefined : !image ? 'background' : undefined"
      :style="patientAvatarBgColor ? { backgroundColor: patientAvatarBgColor } : undefined"
      class="tw:relative tw:overflow-hidden tw:transition-colors tw:duration-300"
      :class="[
        { 'tw:!text-on-surface': !image && !isPatientAvatar },
        { 'tw:!bg-primary': isPatientAvatar && !image && !patientAvatarBgColor },
        mergedAvatarClass,
      ]"
    >
      <v-progress-circular
        v-if="isLoading && !image"
        indeterminate
        size="16"
        width="2"
      />

      <app-img
        v-else-if="image"
        :src="image"
        eager
        cover
      />

      <div
        v-else-if="isPatientAvatar && displayInitials"
        class="tw:flex tw:items-center tw:justify-center tw:w-full tw:h-full tw:font-bold tw:text-white tw:select-none"
        :style="{ fontSize: initialsFontSize }"
      >
        {{ displayInitials }}
      </div>

      <v-icon
        v-else-if="icon && !image"
        :icon="icon"
        :size="iconSize ?? 'x-small'"
        color="primary"
      />

      <v-icon
        v-else-if="!image"
        size="x-small"
        color="secondary"
        icon="mdi-account-outline"
      />
    </v-avatar>

    <div
      v-if="!hideBadges && badges.length"
      class="tw:absolute tw:left-1/2 tw:bottom-0 tw:-translate-x-1/2 tw:translate-y-1/2 tw:flex tw:gap-1 tw:items-center tw:justify-center tw:z-content-overlay"
    >
      <badges :badges="badges" />
    </div>
  </div>
</template>
